import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import CategoryManager from './CategoryManager';
import FormTemplateManager from '../FormTemplateManager';
import PackEditor from '../PackEditor';
import FormLibrary from '../FormLibrary';
import PackRoutingPanel from '../PackRoutingPanel';

const SettingsPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPackEditor, setShowPackEditor] = useState(false);
  const [showFormLibrary, setShowFormLibrary] = useState(false);
  const [showFormTemplates, setShowFormTemplates] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await api.getSettings();
        setSettings(data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        alert('Could not load settings.');
      }
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!window.confirm('Are you sure you want to save these settings?')) return;
    try {
      await api.updateSettings(settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Error saving settings.');
    }
  };

  if (loading) {
    return <div>Loading settings...</div>;
  }

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-bold">Admin Settings</h1>

      {/* Buying Power Settings */}
      <div className="p-4 border rounded-lg">
        <h2 className="text-lg font-semibold mb-4">Buying Power</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Buffer Amount</label>
            <input type="number" name="buffer_amount" value={settings.buffer_amount || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">AR Percentage</label>
            <input type="number" name="ar_percentage" value={settings.ar_percentage || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Floorplan Limit</label>
            <input type="number" name="floorplan_limit" value={settings.floorplan_limit || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
          </div>
          <div className="flex items-center">
            <input type="checkbox" name="include_floorplan_in_buying_power" checked={settings.include_floorplan_in_buying_power === 'true'} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
            <label className="ml-2 block text-sm text-gray-900">Include Floorplan in Buying Power</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" name="show_conservative_to_buyers" checked={settings.show_conservative_to_buyers === 'true'} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
            <label className="ml-2 block text-sm text-gray-900">Show Conservative Buying Power to Buyers</label>
          </div>
        </div>
      </div>

      {/* Pack Fees */}
      <div className="p-4 border rounded-lg">
        <h2 className="text-lg font-semibold mb-4">Pack Fees</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Retail</label>
            <input type="number" name="pack_retail" value={settings.pack_retail || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Wholesale Direct</label>
            <input type="number" name="pack_wholesale_direct" value={settings.pack_wholesale_direct || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Wholesale Auction</label>
            <input type="number" name="pack_wholesale_auction" value={settings.pack_wholesale_auction || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Copart</label>
            <input type="number" name="pack_copart" value={settings.pack_copart || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
          </div>
        </div>
      </div>

      {/* Forms & Print Packs */}
      <div className="p-4 border rounded-lg">
        <h2 className="text-lg font-semibold mb-1">Forms &amp; Print Packs</h2>
        <p className="text-sm text-gray-500 mb-4">
          Packs control which forms print together and in what order. The table below decides
          which pack each kind of deal prints; Titles uses the pack you tap when printing.
        </p>
        <div className="mb-4">
          <PackRoutingPanel />
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowPackEditor(true)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium">
            Pack Editor
          </button>
          <button onClick={() => setShowFormLibrary(true)} className="px-4 py-2 bg-neutral-700 text-white rounded-lg hover:bg-neutral-800 text-sm font-medium">
            Form Library
          </button>
          <button onClick={() => setShowFormTemplates(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
            Form Templates
          </button>
        </div>
      </div>

      {/* Category Management */}
      <div className="p-4 border rounded-lg">
        <h2 className="text-lg font-semibold mb-4">Category Management</h2>
        <CategoryManager />
      </div>

      {/* Aging Thresholds */}
      <div className="p-4 border rounded-lg">
        <h2 className="text-lg font-semibold mb-4">Aging Thresholds (Days)</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Green (Up to)</label>
            <input type="number" name="aging_green" value={settings.aging_green || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Yellow (Up to)</label>
            <input type="number" name="aging_yellow" value={settings.aging_yellow || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Red (Anything Above Yellow)</label>
            <input type="number" name="aging_red" value={settings.aging_red || ''} onChange={handleInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" disabled />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700">
          Save All Settings
        </button>
      </div>

      {/* Forms management modals */}
      {showPackEditor && <PackEditor onClose={() => setShowPackEditor(false)} />}
      {showFormLibrary && <FormLibrary onClose={() => setShowFormLibrary(false)} />}
      {showFormTemplates && <FormTemplateManager onClose={() => setShowFormTemplates(false)} />}
    </div>
  );
};

export default SettingsPage;
